// File: frontend/src/pages/AdminPrivacyPage.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { useAuthContext } from '../contexts/AuthContext'
import { NavLink } from 'react-router-dom'
import * as adminService from '../services/adminService'
import {
  FiInbox,
  FiShield,
  FiUserCheck,
  FiLoader,
  FiRefreshCw,
  FiAlertTriangle,
  FiBarChart2,
  FiDatabase,
  FiMessageSquare,
  FiMenu,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiAward,
} from 'react-icons/fi'

// Re-using the sidebar for a consistent admin experience
const AdminSidebar = ({ user, logout }) => (
  <>
    <nav className="admin-sidebar-nav">
      <NavLink to="/admin/dashboard" className="admin-nav-link">
        <FiBarChart2 size={20} /> Dashboard
      </NavLink>
      <NavLink to="/admin/kb" className="admin-nav-link">
        <FiDatabase size={20} /> Knowledge
      </NavLink>
      {/* Features link - Only show to superadmins */}
      {user?.roles?.includes(
        process.env.REACT_APP_SUPERADMIN_ROLE_NAME || 'superadmin'
      ) && (
        <NavLink to="/admin/features" className="admin-nav-link">
          <FiAward size={20} /> Features
        </NavLink>
      )}
      <NavLink to="/admin/privacy" className="admin-nav-link">
        <FiShield size={20} /> Privacy
      </NavLink>
      <NavLink to="/admin/feedback" className="admin-nav-link">
        <FiInbox size={20} /> Feedback
      </NavLink>
      <NavLink to="/chat" className="admin-nav-link">
        <FiMessageSquare size={20} /> Chat
      </NavLink>
    </nav>
    <div className="admin-sidebar-footer">
      <div className="admin-user-info">
        {user && (
          <p className="admin-user-display-name" title={user.display_name}>
            {user.display_name || user.first_name || 'Utente'}
          </p>
        )}
      </div>
      <div className="admin-sidebar-footer-actions">
        <button onClick={logout} className="admin-icon-button small" title="Esci">
          <FiLogOut />
        </button>
      </div>
    </div>
  </>
)

const AdminPrivacyPage = () => {
  const { token, user, logout } = useAuthContext()
  const [agreements, setAgreements] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isSidebarOpen, setSidebarOpen] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const itemsPerPage = 50

  const isAdmin = user?.roles?.includes(
    process.env.REACT_APP_ADMIN_ROLE_NAME || 'admin'
  )

  const fetchAgreements = useCallback(
    async page => {
      if (!token || !isAdmin) return
      setLoading(true)
      setError('')
      try {
        const skip = (page - 1) * itemsPerPage
        const data = await adminService.getPrivacyAgreements(token, skip, itemsPerPage)
        setAgreements(data.agreements)
        setTotalCount(data.total_count)
      } catch (err) {
        console.error('Failed to fetch privacy agreements:', err)
        setError(
          err.response?.data?.detail ||
            'Si è verificato un errore durante il recupero dei dati.'
        )
      } finally {
        setLoading(false)
      }
    },
    [token, isAdmin]
  )

  useEffect(() => {
    fetchAgreements(currentPage)
  }, [fetchAgreements, currentPage])

  const formatDate = dateString => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const totalPages = Math.ceil(totalCount / itemsPerPage)

  if (!isAdmin) {
    return (
      <div className="admin-page-container">
        <main
          className="admin-main-content"
          style={{ margin: 'auto', maxWidth: '600px' }}
        >
          <div className="admin-card" style={{ textAlign: 'center' }}>
            <FiAlertTriangle size={48} className="danger" />
            <h2>Accesso Negato</h2>
            <p>Non hai i permessi per visualizzare questa pagina.</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="admin-page-container">
      {isSidebarOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'is-open' : ''}`}>
        <div className="admin-sidebar-header">
          <img
            src="/franca-logo.png"
            alt="F.R.A.N.C.A."
            className="admin-sidebar-logo"
          />
        </div>
        <AdminSidebar user={user} logout={logout} />
      </aside>

      <main className="admin-main-content">
        <header className="admin-page-header">
          <button
            className="admin-button mobile-menu-button"
            onClick={() => setSidebarOpen(true)}
          >
            <FiMenu />
          </button>
          <h1>
            <FiShield /> Accettazione Privacy Policy
          </h1>
          <div className="admin-page-header-actions">
            <button
              onClick={() => fetchAgreements(currentPage)}
              disabled={loading}
              className="admin-button secondary"
            >
              <FiRefreshCw className={loading ? 'spinner-icon' : ''} />
              Aggiorna
            </button>
          </div>
        </header>

        {error && <div className="admin-error-message">{error}</div>}

        <section className="admin-card">
          <h2>
            <FiUserCheck /> Utenti che hanno Accettato
          </h2>
          <p>
            Elenco di tutti gli utenti che hanno accettato l'informativa sulla privacy,
            ordinati per data di accettazione più recente.
          </p>

          <div className="admin-table-container">
            <table>
              <thead>
                <tr>
                  <th>Email Utente</th>
                  <th>Nome</th>
                  <th>Versione Accettata</th>
                  <th>Data e Ora Accettazione</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" className="admin-loading-state">
                      <FiLoader className="spinner-icon" /> Caricamento consensi...
                    </td>
                  </tr>
                ) : agreements.length > 0 ? (
                  agreements.map(agreement => (
                    <tr key={agreement.user_id}>
                      <td>{agreement.user_sfre_email}</td>
                      <td>{agreement.user_display_name || 'N/D'}</td>
                      <td>
                        <span className="version-tag">{agreement.policy_version}</span>
                      </td>
                      <td>{formatDate(agreement.agreed_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>
                      Nessun consenso trovato.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!loading && totalCount > itemsPerPage && (
            <div className="pagination-controls">
              <button
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >
                <FiChevronLeft /> Precedente
              </button>
              <span>
                Pagina {currentPage} di {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage === totalPages}
              >
                Successiva <FiChevronRight />
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  )
}

export default AdminPrivacyPage
